import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Shield, Activity, AlertTriangle, SlidersHorizontal, ListChecks, Loader2, RefreshCw, ShieldCheck, Eye, Search } from 'lucide-react';

const LEVEL_COLORS = {
  normal: 'bg-emerald-100 text-emerald-700',
  soft_throttle: 'bg-amber-100 text-amber-700',
  step_up_required: 'bg-orange-100 text-orange-700',
  temp_restricted: 'bg-rose-100 text-rose-700',
  admin_review: 'bg-red-100 text-red-700',
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: Activity },
  { id: 'risk', label: 'Risk Queue', icon: AlertTriangle },
  { id: 'log', label: 'Access Log', icon: Eye },
  { id: 'settings', label: 'Thresholds', icon: SlidersHorizontal },
  { id: 'shadow', label: 'Shadow Report', icon: ListChecks },
];

export default function TalentDbProtection() {
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [restrictions, setRestrictions] = useState([]);
  const [logRows, setLogRows] = useState([]);
  const [logFilter, setLogFilter] = useState('');
  const [settings, setSettings] = useState(null);
  const [shadow, setShadow] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/talent-protection/overview?days=7');
      setOverview(data);
    } catch (e) { toast.error('Failed to load overview'); }
    setLoading(false);
  }, []);

  const loadRestrictions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/talent-protection/restrictions');
      setRestrictions(data.rows || []);
    } catch (e) { toast.error('Failed to load restrictions'); }
    setLoading(false);
  }, []);

  const loadLog = useCallback(async () => {
    setLoading(true);
    try {
      const qs = logFilter ? `?action=${encodeURIComponent(logFilter)}` : '';
      const { data } = await api.get(`/admin/talent-protection/access-log${qs}`);
      setLogRows(data.rows || []);
    } catch (e) { toast.error('Failed to load access log'); }
    setLoading(false);
  }, [logFilter]);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/talent-protection/settings');
      setSettings(data);
    } catch (e) { toast.error('Failed to load settings'); }
    setLoading(false);
  }, []);

  const loadShadow = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/talent-protection/shadow-report?days=14');
      setShadow(data);
    } catch (e) { toast.error('Failed to load shadow report'); }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (tab === 'overview') loadOverview();
    if (tab === 'risk') loadRestrictions();
    if (tab === 'log') loadLog();
    if (tab === 'settings') loadSettings();
    if (tab === 'shadow') loadShadow();
  }, [tab, loadOverview, loadRestrictions, loadLog, loadSettings, loadShadow]);

  const toggleEnforcement = async (val) => {
    if (val && !window.confirm('Enable ENFORCEMENT? This exits shadow mode and will start graduated throttling of accounts that exceed thresholds. Make sure you have reviewed the shadow report.')) return;
    setSavingConfig(true);
    try {
      await api.put('/admin/talent-protection/config', { enforcement_enabled: val });
      toast.success(val ? 'Enforcement ENABLED' : 'Back to shadow mode');
      loadOverview();
    } catch (e) { toast.error('Failed to update config'); }
    setSavingConfig(false);
  };

  const clearRestriction = async (id) => {
    try {
      await api.post(`/admin/talent-protection/restrictions/${id}/clear`);
      toast.success('Restriction cleared');
      loadRestrictions();
    } catch (e) { toast.error('Failed to clear'); }
  };

  const whitelist = async (r) => {
    try {
      await api.post('/admin/talent-protection/restrictions', {
        viewer_user_id: r.viewer_user_id, account_id: r.account_id,
        level: 'normal', is_whitelist: true, reason: 'Admin-trusted account (whitelisted)',
      });
      toast.success('Account whitelisted');
      loadRestrictions();
    } catch (e) { toast.error('Failed to whitelist'); }
  };

  const saveSetting = async (row) => {
    try {
      await api.put('/admin/talent-protection/settings', row);
      toast.success(`Saved ${row.scope}:${row.scope_key}`);
      loadSettings();
    } catch (e) { toast.error('Failed to save'); }
  };

  return (
    <div className="min-h-screen bg-zinc-50 p-6" data-testid="talent-protection-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="h-7 w-7 text-indigo-600" />
          <h1 className="text-2xl font-bold text-zinc-900">Talent Database Protection</h1>
          {overview && (
            <Badge className={overview.enforcement_enabled ? 'bg-rose-100 text-rose-700' : 'bg-sky-100 text-sky-700'} data-testid="enforcement-badge">
              {overview.enforcement_enabled ? 'ENFORCING' : 'SHADOW MODE'}
            </Badge>
          )}
        </div>
        <p className="text-sm text-zinc-500 mb-6">Anti-scraping detection, audit logging, and graduated protection for the paid talent network.</p>

        <div className="flex gap-2 mb-6 flex-wrap">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === t.id ? 'bg-indigo-600 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-100 border border-zinc-200'}`}>
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => setTab(tab)} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>

        {loading && <div className="flex items-center gap-2 text-zinc-500 mb-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}

        {/* OVERVIEW */}
        {tab === 'overview' && overview && (
          <div className="space-y-6">
            <Card>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="h-5 w-5 text-indigo-600" />
                  <div>
                    <div className="font-semibold text-zinc-900">Enforcement</div>
                    <div className="text-xs text-zinc-500">Shadow mode logs + scores without throttling. Enable only after reviewing the shadow report.</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {savingConfig && <Loader2 className="h-4 w-4 animate-spin" />}
                  <Switch checked={overview.enforcement_enabled} onCheckedChange={toggleEnforcement} data-testid="enforcement-toggle" />
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[['Searches', 'searches'], ['Profile Views', 'profile_views'], ['Contact Reveals', 'contact_reveals'], ['Exports', 'exports'], ['Active Viewers', 'active_viewers']].map(([label, key]) => (
                <Card key={key}><CardContent className="p-4">
                  <div className="text-2xl font-bold text-zinc-900" data-testid={`stat-${key}`}>{overview.totals?.[key] ?? 0}</div>
                  <div className="text-xs text-zinc-500">{label} · {overview.window_days}d</div>
                </CardContent></Card>
              ))}
            </div>

            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Search className="h-4 w-4" /> Top Viewers (harvest signature = high views, low engagement)</CardTitle></CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm" data-testid="top-viewers-table">
                  <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase">
                    <tr><th className="text-left p-3">Account</th><th className="p-3">Plan</th><th className="p-3">Views</th><th className="p-3">Distinct</th><th className="p-3">Searches</th><th className="p-3">Favorites</th><th className="p-3">Risk</th><th className="p-3">Would-be</th></tr>
                  </thead>
                  <tbody>
                    {(overview.top_viewers || []).map((v) => (
                      <tr key={v.viewer_user_id} className="border-t border-zinc-100">
                        <td className="p-3 text-left">{v.email || v.viewer_user_id?.slice(0, 8)} <span className="text-zinc-400">({v.role})</span></td>
                        <td className="p-3 text-center">{v.plan_id || '—'}</td>
                        <td className="p-3 text-center font-medium">{v.views}</td>
                        <td className="p-3 text-center">{v.distinct_talent}</td>
                        <td className="p-3 text-center">{v.searches}</td>
                        <td className="p-3 text-center">{v.favorites}</td>
                        <td className="p-3 text-center">{v.risk_score ?? '—'}</td>
                        <td className="p-3 text-center">{v.would_be_level && <Badge className={LEVEL_COLORS[v.would_be_level]}>{v.would_be_level}</Badge>}</td>
                      </tr>
                    ))}
                    {(!overview.top_viewers || overview.top_viewers.length === 0) && <tr><td colSpan={8} className="p-6 text-center text-zinc-400">No discovery activity yet.</td></tr>}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        )}

        {/* RISK QUEUE */}
        {tab === 'risk' && (
          <Card>
            <CardHeader><CardTitle className="text-base">Active Restrictions & Flags</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="restrictions-table">
                <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase">
                  <tr><th className="text-left p-3">Account</th><th className="p-3">Level</th><th className="p-3">Risk</th><th className="p-3">Reason</th><th className="p-3">Set By</th><th className="p-3">Actions</th></tr>
                </thead>
                <tbody>
                  {restrictions.map((r) => (
                    <tr key={r.id} className="border-t border-zinc-100">
                      <td className="p-3 text-left">{r.viewer_email || r.viewer_user_id?.slice(0, 8) || r.account_id?.slice(0, 8)}</td>
                      <td className="p-3 text-center"><Badge className={LEVEL_COLORS[r.level]}>{r.is_whitelist ? 'whitelist' : r.level}</Badge></td>
                      <td className="p-3 text-center">{r.risk_score ?? '—'}</td>
                      <td className="p-3 text-left text-zinc-500 max-w-md">{r.reason}</td>
                      <td className="p-3 text-center">{r.set_by === 'system' ? 'system' : 'admin'}</td>
                      <td className="p-3 text-center whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={() => clearRestriction(r.id)} data-testid={`clear-${r.id}`}>Clear</Button>
                        {!r.is_whitelist && <Button size="sm" variant="outline" className="ml-2" onClick={() => whitelist(r)} data-testid={`whitelist-${r.id}`}>Whitelist</Button>}
                      </td>
                    </tr>
                  ))}
                  {restrictions.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-zinc-400">No active restrictions — nothing flagged.</td></tr>}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* ACCESS LOG */}
        {tab === 'log' && (
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="text-base">Access Log</CardTitle>
              <div className="flex gap-2">
                <select value={logFilter} onChange={(e) => setLogFilter(e.target.value)} className="border border-zinc-200 rounded-md text-sm px-2" data-testid="log-action-filter">
                  <option value="">All actions</option>
                  <option value="search">Search</option>
                  <option value="profile_view">Profile view</option>
                  <option value="contact_reveal">Contact reveal</option>
                  <option value="export">Export</option>
                  <option value="api_talent">API talent</option>
                </select>
                <Button size="sm" variant="outline" onClick={loadLog}>Apply</Button>
              </div>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="access-log-table">
                <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase">
                  <tr><th className="text-left p-3">Time</th><th className="p-3">Viewer</th><th className="p-3">Action</th><th className="p-3">Plan</th><th className="p-3">Source</th><th className="p-3">Result</th><th className="p-3">Risk</th></tr>
                </thead>
                <tbody>
                  {logRows.map((r) => (
                    <tr key={r.id} className="border-t border-zinc-100">
                      <td className="p-3 text-left whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="p-3 text-left">{r.viewer_email || r.viewer_user_id?.slice(0, 8)} <span className="text-zinc-400">({r.viewer_role})</span></td>
                      <td className="p-3 text-center">{r.action}</td>
                      <td className="p-3 text-center">{r.plan_id || '—'}</td>
                      <td className="p-3 text-center">{r.source}</td>
                      <td className="p-3 text-center">{r.result_count ?? '—'}</td>
                      <td className="p-3 text-center">{r.risk_score ?? '—'}</td>
                    </tr>
                  ))}
                  {logRows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-zinc-400">No log rows.</td></tr>}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* SETTINGS */}
        {tab === 'settings' && settings && (
          <div className="space-y-4">
            <Card><CardContent className="p-4 text-sm text-zinc-600">
              Thresholds are <b>configurable by plan</b> and can be <b>overridden per account</b>. Numbers below are provisional shadow-mode seeds — tune them using the Shadow Report before enabling enforcement. Agencies/enterprise intentionally get much higher ceilings.
            </CardContent></Card>
            {(settings.settings || []).map((s, idx) => (
              <Card key={s.id}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="outline">{s.scope}</Badge>
                    <span className="font-semibold text-zinc-900">{s.scope_key}</span>
                    {s.note && <span className="text-xs text-zinc-400">· {s.note}</span>}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[['hourly_search_cap', 'Hourly searches'], ['daily_profile_view_cap', 'Daily views'], ['daily_distinct_talent_cap', 'Daily distinct talent'], ['burst_per_minute', 'Burst / min'], ['daily_export_cap', 'Daily exports'], ['risk_soft_threshold', 'Risk soft'], ['risk_restrict_threshold', 'Risk restrict']].map(([k, label]) => (
                      <div key={k}>
                        <label className="text-xs text-zinc-500">{label}</label>
                        <Input type="number" defaultValue={s[k] ?? ''} data-testid={`setting-${s.scope_key}-${k}`}
                          onChange={(e) => { settings.settings[idx][k] = e.target.value === '' ? null : Number(e.target.value); }} />
                      </div>
                    ))}
                    <div className="flex items-end gap-2">
                      <div>
                        <label className="text-xs text-zinc-500 block">Export enabled</label>
                        <Switch checked={!!s.export_enabled} onCheckedChange={(v) => { settings.settings[idx].export_enabled = v; setSettings({ ...settings }); }} />
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button size="sm" onClick={() => saveSetting(settings.settings[idx])} data-testid={`save-setting-${s.scope_key}`}>Save</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* SHADOW REPORT */}
        {tab === 'shadow' && shadow && (
          <div className="space-y-6">
            <Card><CardContent className="p-4">
              <div className="text-sm text-zinc-600 mb-3">{shadow.note}</div>
              <div className="text-sm text-zinc-500 mb-4">Evaluated <b>{shadow.accounts_evaluated}</b> accounts over the last {shadow.window_days} days.</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {Object.entries(shadow.would_trip || {}).map(([k, v]) => (
                  <Card key={k}><CardContent className="p-4">
                    <div className="text-2xl font-bold text-zinc-900">{v.pct}%</div>
                    <div className="text-xs text-zinc-500">{k.replace(/_/g, ' ')} ({v.count})</div>
                  </CardContent></Card>
                ))}
              </div>
            </CardContent></Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Would-be flagged accounts</CardTitle></CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm" data-testid="shadow-flagged-table">
                  <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase">
                    <tr><th className="text-left p-3">Account</th><th className="p-3">Plan</th><th className="p-3">Peak Views</th><th className="p-3">Peak Distinct</th><th className="p-3">Peak Searches</th><th className="p-3">Peak Risk</th><th className="p-3">Would-be</th></tr>
                  </thead>
                  <tbody>
                    {(shadow.flagged_accounts || []).map((f) => (
                      <tr key={f.viewer_user_id} className="border-t border-zinc-100">
                        <td className="p-3 text-left">{f.viewer_user_id?.slice(0, 8)}</td>
                        <td className="p-3 text-center">{f.plan_id}</td>
                        <td className="p-3 text-center">{f.peak_views}</td>
                        <td className="p-3 text-center">{f.peak_distinct}</td>
                        <td className="p-3 text-center">{f.peak_searches}</td>
                        <td className="p-3 text-center">{f.peak_risk}</td>
                        <td className="p-3 text-center"><Badge className={LEVEL_COLORS[f.level]}>{f.level}</Badge></td>
                      </tr>
                    ))}
                    {(!shadow.flagged_accounts || shadow.flagged_accounts.length === 0) && <tr><td colSpan={7} className="p-6 text-center text-zinc-400">No accounts would be flagged with current thresholds.</td></tr>}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
